import type { StreamHandle } from './audio-engine.js';
import { mixer } from './audio-mixer.js';
import { COMBAT_MUSIC_DB, decibels } from './mix.js';

type Mode = 'explore' | 'combat';

const urls = (files: Record<string, string>) => Object.keys(files).sort().map(path => files[path]);
const SETS: Record<Mode, string[]> = {
  explore: urls(import.meta.glob('../assets/audio/music/explore-*.mp3', { eager: true, query: '?url', import: 'default' }) as Record<string, string>),
  combat: urls(import.meta.glob('../assets/audio/music/combat-*.mp3', { eager: true, query: '?url', import: 'default' }) as Record<string, string>),
};
const FADE_IN_MS: Record<Mode, number> = { explore: 5000, combat: 800 };
/** Keyed by the track that is leaving: a fight cuts the exploring music quickly, while the fight music lingers after it ends. */
const FADE_OUT_MS: Record<Mode, number> = { explore: 1000, combat: 6000 };
const RETRY_MS = 2000;
/** Each kind's level at the top of its fade: combat music stands up to the fight (`mix.ts`). */
const LEVEL: Record<Mode, number> = { explore: 1, combat: decibels(COMBAT_MUSIC_DB) };

interface Playing { mode: Mode; url: string; track: StreamHandle; level: number; from: number; to: number; started: number; ms: number }

/**
 * Plays one of the exploration tracks at random while the party is out in the world, crossfades to one of the combat tracks
 * while a fight is on and back when it ends, never repeating the track just played. A track that finishes is followed by
 * another random pick of the same kind.
 */
export class GameMusic {
  private tracks = new Map<string, StreamHandle>();
  private mode: Mode | null = null;
  private current: Playing | null = null;
  /** The current track and any still fading out, all stepped by one timer. */
  private active: Playing[] = [];
  private upcoming: Partial<Record<Mode, string>> = {};
  private last: Partial<Record<Mode, string>> = {};
  private retryAt = 0;
  private fade: ReturnType<typeof setInterval> | undefined;

  /** Fetches the first exploration and combat tracks one after another, so multi-megabyte files never split the connection with each other or the scene. */
  async preload() {
    for (const mode of ['explore', 'combat'] as const) await this.warm(this.upcomingTrack(mode));
  }

  /** Called with every snapshot's combat flag; only a change of kind (or a play the browser blocked earlier) does anything. */
  update(inCombat: boolean) {
    const mode: Mode = inCombat ? 'combat' : 'explore';
    if (mode === this.mode && (this.current || performance.now() < this.retryAt)) return;
    this.begin(mode);
  }

  /** Fades everything out; the next `update` starts music again. */
  stop() {
    this.mode = null; this.current = null;
    for (const playing of this.active) this.fadeTo(playing, 0, FADE_OUT_MS.combat);
  }

  private warm(url: string) {
    return new Promise<void>(resolve => {
      const audio = this.stream(url).element; audio.preload = 'auto';
      const finish = () => { clearTimeout(timer); audio.removeEventListener('canplaythrough', finish); audio.removeEventListener('error', finish); resolve(); };
      const timer = setTimeout(finish, 15_000);
      audio.addEventListener('canplaythrough', finish); audio.addEventListener('error', finish);
      audio.load();
    });
  }

  /** The track for `url`, made once and routed through the music bus. */
  private stream(url: string) {
    let track = this.tracks.get(url);
    if (!track) {
      const made = track = mixer.engine.stream(url, 'music');
      made.element.addEventListener('ended', () => { if (this.current?.track === made && this.mode) this.begin(this.mode); });
      this.tracks.set(url, made);
    }
    return track;
  }

  /** The track a mode plays next. It is chosen ahead so it can load, and is never the one just played. */
  private upcomingTrack(mode: Mode) {
    return this.upcoming[mode] ??= this.choose(mode);
  }

  private choose(mode: Mode) {
    const set = SETS[mode], busy = new Set([this.last[mode], ...this.active.map(playing => playing.url)]);
    const free = set.filter(url => !busy.has(url)), pool = free.length ? free : set;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  private fadeTo(playing: Playing, to: number, ms: number) {
    playing.from = playing.level; playing.to = to; playing.started = performance.now(); playing.ms = ms;
    this.fade ??= setInterval(() => this.step(), 50);
  }

  private step() {
    const now = performance.now();
    for (const playing of [...this.active]) {
      const progress = Math.min(1, (now - playing.started) / playing.ms);
      playing.level = playing.from + (playing.to - playing.from) * progress;
      playing.track.level(playing.level * LEVEL[playing.mode]);
      if (progress === 1 && playing.to === 0) this.drop(playing);
    }
    if (this.active.every(playing => playing.to === 1 && playing.level === 1)) { clearInterval(this.fade); this.fade = undefined; }
  }

  private drop(playing: Playing) {
    playing.track.element.pause();
    const index = this.active.indexOf(playing);
    if (index >= 0) this.active.splice(index, 1);
  }

  private begin(mode: Mode) {
    const leaving = this.current;
    this.mode = mode; this.current = null;
    if (leaving) this.fadeTo(leaving, 0, FADE_OUT_MS[leaving.mode]);
    let url = this.upcomingTrack(mode);
    if (this.active.some(playing => playing.url === url)) url = this.choose(mode);
    this.last[mode] = url; this.upcoming[mode] = this.choose(mode);
    const track = this.stream(url), audio = track.element;
    const playing: Playing = this.current = { mode, url, track, level: 0, from: 0, to: 1, started: performance.now(), ms: FADE_IN_MS[mode] };
    this.active.push(playing);
    audio.currentTime = 0; track.level(0);
    this.fadeTo(playing, 1, FADE_IN_MS[mode]);
    audio.play().then(() => {
      // Start loading the track after this one while this one plays.
      const next = this.stream(this.upcoming[mode]!).element; next.preload = 'auto'; next.load();
    }, () => {
      /* Blocked until a gesture; the next update after a short pause tries again. */
      this.drop(playing);
      if (this.current === playing) { this.current = null; this.retryAt = performance.now() + RETRY_MS; }
    });
  }
}
